"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Phone } from "lucide-react";
import { HeroOrganicMask } from "@/components/decorative/HeroOrganicMask";
import { PageDecorLayer } from "@/components/decorative/PageDecorLayer";
import { EASE_OUT_EXPO } from "@/components/motion/constants";
import { useScrollContext } from "@/components/motion/SmoothScrollProvider";
import { assets } from "@/content/assets";
import { site } from "@/content/site";
import { cn } from "@/lib/utils";

const quickChips = [
  {
    icon: Phone,
    label: site.contact.phone,
    href: site.contact.phoneHref,
    accent: false,
    external: false,
  },
  {
    icon: MapPin,
    label: site.address.line2,
    href: site.mapsUrl,
    accent: true,
    external: true,
  },
] as const;

function QuickChip({
  icon: Icon,
  label,
  href,
  accent,
  external = false,
}: {
  icon: typeof Phone;
  label: string;
  href?: string;
  accent?: boolean;
  external?: boolean;
}) {
  const content = (
    <>
      <span
        className={cn(
          "contact-icon-circle h-10 w-10",
          accent && "contact-icon-circle-accent"
        )}
      >
        <Icon size={18} className="m-auto" aria-hidden />
      </span>
      <span className="text-sm font-medium text-[var(--contact-text)]">{label}</span>
    </>
  );

  if (href && external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="contact-chip"
      >
        {content}
      </a>
    );
  }

  if (href) {
    return (
      <Link href={href} className="contact-chip">
        {content}
      </Link>
    );
  }

  return <div className="contact-chip">{content}</div>;
}

export function ContactPremiumHero() {
  const reduced = useReducedMotion();
  const { scrollToElement } = useScrollContext();

  const fadeUp = (delay = 0) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, ease: EASE_OUT_EXPO, delay },
        };

  const scrollToForm = () => {
    scrollToElement("#contact-form", { offset: -96 });
  };

  return (
    <section
      aria-label="Contact hero"
      className="relative mt-16 overflow-hidden pb-10 pt-12 md:pb-14 md:pt-16"
    >
      <PageDecorLayer variant="hero" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-8 px-4 md:px-6 lg:grid-cols-2 lg:items-center lg:gap-12">
        <div>
          <motion.p
            {...fadeUp(0)}
            className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--contact-accent)]"
          >
            Contact Us
          </motion.p>

          <motion.h1
            {...fadeUp(0.05)}
            className="font-display text-4xl font-bold leading-tight text-[var(--contact-text)] md:text-5xl lg:text-6xl xl:text-7xl"
          >
            Get In{" "}
            <span className="bg-gradient-to-r from-[var(--contact-primary)] to-[var(--contact-gold)] bg-clip-text text-transparent">
              Touch
            </span>
          </motion.h1>

          <motion.p
            {...fadeUp(0.1)}
            className="mt-5 max-w-xl text-lg leading-relaxed text-[var(--contact-muted)]"
          >
            Join us in making a difference in our communities. We would love to
            hear from you.
          </motion.p>

          <motion.div
            {...fadeUp(0.2)}
            className="mt-8 flex flex-wrap gap-4"
          >
            <button
              type="button"
              onClick={scrollToForm}
              className="contact-btn-primary"
            >
              Send a Message
            </button>
            <Link
              href={site.contact.phoneHref}
              className="contact-btn-secondary"
            >
              <Phone size={18} aria-hidden />
              Call Us
            </Link>
          </motion.div>

          <motion.div
            {...fadeUp(0.3)}
            className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {quickChips.map((chip) => (
              <QuickChip key={chip.label} {...chip} />
            ))}
          </motion.div>
        </div>

        <div className="relative">
          <HeroOrganicMask
            src={assets.contactHero}
            alt="Volunteers serving meals to community members"
            imageClassName="object-[center_30%]"
            priority
            className="relative z-10"
          />
        </div>
      </div>
    </section>
  );
}
